export const IDENTIFY_CATEGORIES = [
  {
    name: 'Furniture',
    description:
      'Movable furniture, mirrors, screens and garden furniture of any period or material. Not lamps, clocks, pianos or rugs.',
  },
  {
    name: 'Silver & Metalware',
    description:
      'Silver, silverplate, pewter, brass, copper, bronze and iron objects for the table, hearth, desk and display. Not metal sculpture, jewelry, lighting or kitchen cookware.',
  },
  {
    name: 'Ceramics',
    description:
      'Porcelain, pottery and stoneware of every origin, Asian included: tableware, vases, figurines, tiles and decorative wares. Not ceramic lamps or kitchen bakeware.',
  },
  {
    name: 'Glass',
    description:
      'Glass tableware, vases, bottles, paperweights, figures and stained glass panels. Not lamps, mirrors or glass ovenware.',
  },
  {
    name: 'Clocks & Watches',
    description:
      'Clocks of every kind, pocket watches and wristwatches. Not barometers or other instruments.',
  },
  {
    name: 'Jewelry',
    description:
      'Worn jewelry in fine and costume materials, and loose stones. Not watches or dresser sets.',
  },
  {
    name: 'Paintings & Drawings',
    description:
      'Unique hand-made pictures in any medium: oils, acrylics, watercolors, gouache, pastels, drawings and Asian scroll paintings. Not prints, posters or photographs.',
  },
  {
    name: 'Prints & Multiples',
    description:
      'Images printed in multiples, signed or not: etchings, lithographs, woodcuts, screenprints, posters and reproductions. Not photographs or original drawings.',
  },
  {
    name: 'Photographs',
    description:
      'Photographic images in any process or format, from daguerreotypes to snapshots, slides and negatives. Not cameras or photo reproductions in books.',
  },
  {
    name: 'Sculpture',
    description:
      'Three-dimensional art in bronze, spelter, stone, wood, plaster, resin, jade or ivory, including Asian carvings, decoys and carved masks. Not ceramic or glass figurines.',
  },
  {
    name: 'Books, Maps & Manuscripts',
    description:
      'Books, maps, atlases, globes, handwritten and printed documents, autographs and paper ephemera. Not framed prints or photographs.',
  },
  {
    name: 'Rugs & Textiles',
    description:
      'Rugs, carpets, quilts, coverlets, needlework, lace and household linens. Not clothing or upholstered furniture.',
  },
  {
    name: 'Lighting',
    description:
      'Lamps, chandeliers, sconces, lanterns and lamp shades, electric or oil. Not candlesticks or candelabra.',
  },
  {
    name: 'Tools & Scientific Instruments',
    description:
      'Hand, power and garden tools, and measuring, optical, navigational, drafting and medical instruments. Not cameras or clocks.',
  },
  {
    name: 'Household & Kitchenware',
    description:
      'Everyday cookware, bakeware, kitchen tools, food storage, baskets, frames and home accessories, whatever the material. Not serving and display pieces, which go by material, and not electric appliances.',
  },
  {
    name: 'Electronics & Appliances',
    description:
      'Radios, TVs, audio equipment, cameras, phones, computers, office machines and household appliances. Not lamps, clocks, power tools or electric instruments.',
  },
  {
    name: 'Toys, Games & Collectables',
    description:
      'Toys, dolls, games, coins, stamps, militaria, knives, sports and advertising memorabilia, and records. Not figurines, which go by material.',
  },
  {
    name: 'Clothing & Accessories',
    description:
      'Clothing, furs, shoes, hats, handbags, scarves, fans, eyewear and luggage. Not jewelry, watches or household textiles.',
  },
  {
    name: 'Musical Instruments',
    description:
      'Pianos, organs, string, wind, brass and percussion instruments, music boxes and instrument amplifiers. Not sheet music or records.',
  },
  {
    name: 'Other',
    description:
      'Anything no other category fits: sporting goods, vehicles, natural history, religious and ethnographic objects, architectural salvage, smoking accessories.',
  },
] as const;

export const IDENTIFY_RULES = [
  'Classify by what the object is, not what it shows: a painting of a vase is Paintings & Drawings, a print of a ship is Prints & Multiples.',
  'Drawings and watercolors are folded into Paintings & Drawings, with the medium in technique. In a photo under glass, watercolor, gouache, pastel and drawing are hard to tell apart, so a medium guess should not become a category error.',
  'Pictures printed in multiples go to Prints & Multiples even when signed, numbered or hand-colored. Camera-made pictures go to Photographs. Pictures bound in a book stay with the book.',
  'There is no Asian Works of Art category. Asian objects go by form and material (Chinese porcelain to Ceramics, a woodblock print to Prints & Multiples), with origin and period in the description (Ming, Kangxi, Edo, Meiji, Chinese Export). An Asian category would overlap every other one; the description keeps Asian pieces findable.',
  'A reign mark on Chinese porcelain often copies an earlier reign. Do not set a period from the mark alone.',
  'Ceramic figures go to Ceramics, glass figures to Glass. Sculpture is three-dimensional art in bronze, spelter, stone, wood, plaster, resin, jade or ivory.',
  'Other metal objects (candlesticks, bookends, doorstops, fireplace tools) go to Silver & Metalware unless they are sculpture, jewelry, lighting or cookware.',
  'Candlesticks and candelabra go by material. Lighting is for lamps, fixtures, lanterns and shades.',
  'Cookware, bakeware and kitchen tools go to Household & Kitchenware whatever the material. Serving and display pieces go by material.',
  'Anything electric or battery-powered goes to Electronics & Appliances, except lamps, clocks, power tools, electric instruments and toys, which keep their own categories.',
  'Tall case clocks, pianos and floor lamps are not Furniture. Mirrors and screens are.',
  'Household textiles go to Rugs & Textiles. Anything worn or carried, other than jewelry and watches, goes to Clothing & Accessories.',
  'Clothing & Accessories and Musical Instruments are added: every estate has them, estate sales and auction houses sell them as separate sections, and no other category fits them.',
  'Coins, stamps, militaria, weapons, sports and advertising memorabilia, and records go to Toys, Games & Collectables. Letters, documents, autographs, postcards and paper ephemera go to Books, Maps & Manuscripts.',
  'Carved figures and masks of any culture are Sculpture. Other ethnographic objects go to Other.',
  "A set (tea service, dinner service, flatware service) is one item under the set's term, in the category of its material.",
  'Give a style in the description only when form or decoration shows it; leave it out rather than guess. A revival or reproduction takes the style it imitates, and the description says it is later.',
  'Use a listed term when one fits. Otherwise give the plainest common name rather than stretching a listed term.',
  'Use Other only when no category fits.',
];
